// =====================================================================
// Securite.jsx — M-010 : vérifications, plan, contrôles légaux
// =====================================================================
// Séparé des ACCIDENTS à la demande de Florent (26/08). Tout ce qui touche
// aux blessures vit maintenant dans /accidents — ici on ne trouve que des
// équipements et des échéances, jamais une donnée de santé.
//
// Ce que ça change concrètement : « qui peut ouvrir le registre médical »
// devient une question à un seul endroit, au lieu d'un onglet parmi six.
// Et celui qui trouve un collègue blessé n'a plus à chercher une tuile
// nommée « Sécurité & routines ».
//
// Ce qui reste partagé avec /accidents : le plan, les objets, les actions
// correctives. Un seul autocollant sert les deux flux — l'étiquette d'une
// trousse mène aux accidents, celle d'un extincteur ou d'un rack mène ici.
// 23-10-2026 (Silviu) : plus de lien « Déclarer un accident » sur cet écran —
// les accidents ont leur module (tuile du lanceur, autocollant des trousses).
//
// Ce fichier est volontairement mince : chaque onglet est un écran à part
// entière, déjà écrit et testé séparément.
// =====================================================================

import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth.jsx';
import Breadcrumbs from '../components/Breadcrumbs.jsx';
// 23-10-2026 : la tournée se fait PAR VÉRIFICATION — « Mes vérifications » pour chacun,
// « Tournée » pour qui répartit (Vincent), « Procédures » pour qui en a reçu le droit.
import SecuriteMesVerifications from './SecuriteMesVerifications.jsx';
import SecuriteTournee from './SecuriteTournee.jsx';
import SecuriteProcedures from './SecuriteProcedures.jsx';
import SecuritePlan from './SecuritePlan.jsx';
import SecuriteLegal from './SecuriteLegal.jsx';
import SecuriteEtiquettes from './SecuriteEtiquettes.jsx';
import { codeScanne } from '../lib/codeScanne.js';

const C = { line: '#e5e7eb', mute: '#7b8494', brand: '#714B67' };

const SOUS_TITRE = {
  mes: 'Les vérifications qui vous sont confiées. « Commencer », la réponse, « Terminé » : le temps va tout seul dans vos tâches.',
  tournee: 'Qui fait quelle vérification. Une ligne KO ou HS ouvre automatiquement une action corrective.',
  procedures: 'Les vérifications de chaque tournée : texte, ordre, fréquence, tâche du temps passé.',
  plan: 'Le plan du site : ce qui reste à vérifier, et ce qui est signalé.',
  legal: 'L’échéancier des contrôles périodiques — le premier document que demande l’inspection.',
  etiquettes: 'Les autocollants à coller sur les objets. L’appareil photo du téléphone suffit.',
};

export default function Securite() {
  const { can } = useAuth();
  const canPlan = can('maintenance.plan.all');
  const canConfigure = can('maintenance.configure.all');

  const [tab, setTab] = useState('mes');

  // Arrivée par l'autocollant d'un objet à vérifier : /securite?scan=EXT-03.
  // On ouvre le plan, qui sait sélectionner l'objet par son code. L'URL est
  // nettoyée pour qu'un rafraîchissement ne rejoue pas le scan.
  const [scanCode, setScanCode] = useState(null);
  useEffect(() => {
    const c = codeScanne(new URLSearchParams(window.location.search).get('scan'));
    if (!c) return;
    window.history.replaceState({}, '', window.location.pathname);
    setScanCode(c);
    setTab('plan');
  }, []);

  const onglets = [
    ['mes', 'Mes vérifications'],
    ...(canPlan ? [['tournee', 'Tournée']] : []),
    ...(canConfigure ? [['procedures', 'Procédures']] : []),
    ['plan', 'Plan'],
    ['legal', 'Contrôles légaux'],
    ...(canPlan ? [['etiquettes', 'Étiquettes QR']] : []),
  ];

  return (
    <div style={{ padding: '1.2rem 1.4rem', maxWidth: 1500, margin: '0 auto' }}>
      <Breadcrumbs items={[{ label: 'Sécurité & routines' },
        { label: Object.fromEntries(onglets)[tab] }]} />

      <h1 style={{ fontSize: '1.4rem', margin: '0 0 4px', color: C.brand }}>
        Sécurité &amp; routines
      </h1>
      <p style={{ margin: '0 0 1.1rem', color: C.mute, fontSize: '0.9rem' }}>
        {SOUS_TITRE[tab]}
      </p>

      <div style={{ display: 'flex', gap: 4, marginBottom: '1.1rem', flexWrap: 'wrap' }}>
        {onglets.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} style={{
            border: 'none', borderRadius: 6, padding: '0.5rem 0.9rem', cursor: 'pointer',
            fontWeight: 600, fontSize: '0.88rem',
            background: tab === k ? '#eef2ff' : 'transparent',
            color: tab === k ? '#4338ca' : '#475569',
          }}>{l}</button>
        ))}
      </div>

      {tab === 'mes' && <SecuriteMesVerifications />}
      {tab === 'tournee' && canPlan && <SecuriteTournee />}
      {tab === 'procedures' && canConfigure && <SecuriteProcedures onAllerTournee={canPlan ? () => setTab('tournee') : null} />}
      {tab === 'plan' && <SecuritePlan initialScan={scanCode} />}
      {tab === 'legal' && <SecuriteLegal />}
      {tab === 'etiquettes' && canPlan && <SecuriteEtiquettes />}
    </div>
  );
}
